import { useTranslation } from "react-i18next";
import { FormField, SelectInput, TextInput } from "@/components/ui/primitives";

export const FREEZE_PRESET_OPTIONS = ["none", "7", "28", "90", "180", "other"] as const;
export type FreezePreset = (typeof FREEZE_PRESET_OPTIONS)[number];

export function Field({ id, label, value, onChange, placeholder, type = "text", required = false }: { id: string; label: string; value: string; onChange: (value: string) => void; placeholder: string; type?: "text" | "number" | "date"; required?: boolean }) {
  return (
    <FormField label={label} htmlFor={id} required={required}>
      <TextInput id={id} type={type} value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} />
    </FormField>
  );
}

export function SelectField({ id, label, value, onChange, options, required = false }: { id: string; label: string; value: string; onChange: (value: string) => void; options: Array<{ label: string; value: string }>; required?: boolean }) {
  return (
    <FormField label={label} htmlFor={id} required={required}>
      <SelectInput id={id} value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">--</option>
        {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </SelectInput>
    </FormField>
  );
}

export function FreezeTimeField({ id, label, preset, value, onPresetChange, onCustomValueChange, layout = "stacked" }: { id: string; label: string; preset: FreezePreset; value: string; onPresetChange: (value: FreezePreset) => void; onCustomValueChange: (value: string) => void; layout?: "stacked" | "grid" }) {
  const { t } = useTranslation(["savingPockets", "common"]);
  const options = [
    { value: "none", label: t("savingPockets.none") },
    { value: "7", label: t("savingPockets.freezePresetDays", { count: 7 }) },
    { value: "28", label: t("savingPockets.freezePresetDays", { count: 28 }) },
    { value: "90", label: t("savingPockets.freezePresetDays", { count: 90 }) },
    { value: "180", label: t("savingPockets.freezePresetDays", { count: 180 }) },
    { value: "other", label: t("savingPockets.freezePresetOther") },
  ];

  const fields = <>
    <SelectField id={`${id}-preset`} label={label} value={preset} onChange={(nextValue) => onPresetChange(nextValue as FreezePreset)} options={options} />
    {preset === "other" ? <Field id={`${id}-custom`} label={label} type="number" value={value} onChange={onCustomValueChange} placeholder={t("savingPockets.freezePlaceholder")} /> : null}
  </>;
  return layout === "grid" ? fields : <div className="space-y-3">{fields}</div>;
}

export function ToggleField({ label, checked, onChange }: { label: string; description: string; checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm font-medium text-[var(--app-text)]">{label}</span>
      <button
        type="button"
        aria-label={label}
        aria-pressed={checked}
        onClick={() => onChange(!checked)}
        className="app-focus-ring relative inline-flex h-11 w-12 shrink-0 items-center rounded-full"
      >
        <span className={`relative inline-flex h-7 w-full items-center rounded-full transition ${checked ? "bg-[var(--app-primary)]" : "bg-[var(--app-border)]"}`}>
          <span className={`absolute top-1 h-5 w-5 rounded-full transition ${checked ? "left-6 bg-[var(--app-on-primary)]" : "left-1 bg-[var(--app-text)]"}`} />
        </span>
      </button>
    </div>
  );
}
